import moment from 'moment';

import navigationService from './navigationService';
import storageService from './storageService';
import utilsService from './utilsService';

import {
  BUCKET_SUB_MENU_ITEMS,
  COMMUNICATION_PATH,
} from '../components/expertDashboardModule/expertSettingsComponent/leftNavigationData';
import VIDABOO_CONSTANTS from '../config/consts';

const service = {};

service.addDefaultValuesToAccountDto = (accountDto) => {
  if (accountDto.accountAppCtrlDto.isEngagerEnabled === null) {
    accountDto.accountAppCtrlDto.isEngagerEnabled = true;
  }
};

service.addDefaultValuesToWidgetDtos = (
  accountDto,
  widgetDtos,
  currentUserId,
) => {
  const userIdToEmail = {};

  const usersDataPermissionStatus = service.getUsersDataPermissionStatus(
    accountDto.usersCtrlMap || {},
    currentUserId,
  );

  for (const userId in accountDto.usersCtrlMap) {
    userIdToEmail[userId] = {
      email: accountDto.usersCtrlMap[userId].userDto?.email,
      firstName: accountDto.usersCtrlMap[userId].userDto?.firstName,
      lastName: accountDto.usersCtrlMap[userId].userDto?.lastName,
      privateDataConsentStatus: usersDataPermissionStatus[userId],
    };
  }

  widgetDtos.forEach((widgetDto) => {
    if (!widgetDto.widgetAppCtrlDto.uiLanguage) {
      widgetDto.widgetAppCtrlDto.uiLanguage = 'ENGLISH';
    }
    if (!widgetDto.widgetAppCtrlDto.availabilityFilter) {
      widgetDto.widgetAppCtrlDto.availabilityFilter = 'OFFLINE';
    }
    if (!widgetDto.widgetAppCtrlDto.closedWidgetType) {
      widgetDto.widgetAppCtrlDto.closedWidgetType =
        VIDABOO_CONSTANTS.WIDGET_MASK_TYPE.LOGO;
    }
    if (!widgetDto.widgetAppCtrlDto.style) {
      widgetDto.widgetAppCtrlDto.style = {};
    }
    if (!widgetDto.widgetAppCtrlDto.style.locationOnScreen) {
      widgetDto.widgetAppCtrlDto.style.locationOnScreen =
        VIDABOO_CONSTANTS.WIDGET_LOCATION.BOTTOM_RIGHT;
    }
    if (!widgetDto.widgetAppCtrlDto.style.themePreset) {
      widgetDto.widgetAppCtrlDto.style.themePreset =
        VIDABOO_CONSTANTS.MAIN_THEME.CONSOLTO_LITE.type;
    }

    utilsService.parseStyleObject(widgetDto.widgetAppCtrlDto.style);
    utilsService.assignThemeCustomizationValues(
      widgetDto.widgetAppCtrlDto.style,
    );

    for (const mentorId in widgetDto.mentorsControlMapDto) {
      const extendedMentorDto =
        widgetDto.mentorsControlMapDto[mentorId].extendedMentorDto;
      utilsService.parseStyleObject(
        extendedMentorDto.mentorDto.appControlDto.style,
      );
      const userId = extendedMentorDto.mentorDto.mentorOwnerId;
      extendedMentorDto.email =
        userIdToEmail[userId]?.email || 'Email not found';
      extendedMentorDto.firstName = userIdToEmail[userId]?.firstName || '';
      extendedMentorDto.lastName = userIdToEmail[userId]?.lastName || '';
      extendedMentorDto.privateDataConsentStatus =
        userIdToEmail[userId].privateDataConsentStatus ??
        VIDABOO_CONSTANTS.PRIVATE_DATA_ACCESS_STATES.REQUEST;
      extendedMentorDto.mentorPillColor = service.getMentorPillColor(
        extendedMentorDto.mentorDto.serialNum,
      );
      extendedMentorDto.widgetRole = service.getWidgetAdminRole(
        widgetDto,
        userId,
      );
    }

    for (const teamMentorId in widgetDto.teamMentorsMapDto) {
      const extendedMentorDto =
        widgetDto.teamMentorsMapDto[teamMentorId].extendedMentorDto;
      utilsService.parseStyleObject(
        extendedMentorDto.mentorDto.appControlDto.style,
      );
      extendedMentorDto.mentorPillColor = service.getMentorPillColor(
        extendedMentorDto.mentorDto.serialNum,
      );
    }
  });
};

service.canDeleteAgent = (widgetDtos = []) => {
  const totalAgentsInAllWidgetDtos = widgetDtos.reduce(
    (totalAgents, widgetDto) => {
      const mentorsControlMapDto = widgetDto.mentorsControlMapDto ?? {};
      const totalAgentsInWidget = Object.values(mentorsControlMapDto).length;
      return totalAgents + totalAgentsInWidget;
    },
    0,
  );
  return totalAgentsInAllWidgetDtos > 1;
};

service.checkIfSelectedAgentInactive = (selectedMentorDtos) => {
  const selectedWidgetMentorCtrlDto =
    service.getSelectedWidgetMentorCtrlDto(selectedMentorDtos);
  return !selectedWidgetMentorCtrlDto?.extendedMentorDto.mentorDto.isActive;
};

service.checkIfSelectedWidgetInactive = (selectedMentorDtos) => {
  const selectedWidgetDto = service.getSelectedWidgetDto(selectedMentorDtos);
  return !selectedWidgetDto.isActive;
};

service.checkIfSingleWidgetWithNoAgentSkin = (selectedMentorDtos) => {
  let isSingleWidgetWithNoAgentSkin = false;
  const widgetDtos = Object.values(selectedMentorDtos || {});
  if (
    widgetDtos.length === 1 &&
    !Object.keys(widgetDtos[0].mentorsControlMapDto || {}).length
  ) {
    isSingleWidgetWithNoAgentSkin = true;
  }
  return isSingleWidgetWithNoAgentSkin;
};

service.createNewMarketingElementsStatus = () => {
  return {
    [VIDABOO_CONSTANTS.MARKETING_ELEMENTS.SPLASH_SCREEN]:
      VIDABOO_CONSTANTS.MARKETING_ELEMENT_STATUS.INIT,
    [VIDABOO_CONSTANTS.MARKETING_ELEMENTS.ONBOARDING_FLOW]:
      VIDABOO_CONSTANTS.MARKETING_ELEMENT_STATUS.INIT,
    [VIDABOO_CONSTANTS.MARKETING_ELEMENTS.PRODUCT_TOUR]:
      VIDABOO_CONSTANTS.MARKETING_ELEMENT_STATUS.INIT,
    [VIDABOO_CONSTANTS.MARKETING_ELEMENTS.FRILL_COMPONENT]:
      VIDABOO_CONSTANTS.MARKETING_ELEMENT_STATUS.INIT,
  };
};

const findPeerRoomInfo = (peerParticipantId, chatRooms, selectedMentorIds) => {
  const chatRoomWithPeer = chatRooms?.find((chatRoom) => {
    return (
      chatRoom.ownerObjectType ===
        VIDABOO_CONSTANTS.CHAT_ROOM_OWNER_TYPE.MENTOR &&
      chatRoom.roomType === VIDABOO_CONSTANTS.CHAT_ROOM_TYPES.ONE_ON_ONE &&
      selectedMentorIds.includes(chatRoom.ownerObjectId) &&
      chatRoom.participants.find(
        (participant) =>
          participant.objectType ===
            VIDABOO_CONSTANTS.CHAT_PARTICIPANT_TYPES.GUEST &&
          participant.objectId === peerParticipantId,
      ) &&
      chatRoom.chatRoomPeerInfoDto
    );
  });
  return chatRoomWithPeer;
};

service.getAccountManagerUserId = (
  currentUserId,
  isAccountManager,
  usersCtrlMap,
) => {
  const accountMangerUserId = isAccountManager
    ? currentUserId
    : Object.keys(usersCtrlMap)?.find(
        (userId) => usersCtrlMap[userId].isAccountManager,
      );
  return accountMangerUserId;
};

service.getAllWidgetDtoAndExMentorDtosByUserId = (
  userId = '',
  widgetDtos = [],
) => {
  let widgetAndExMentorDtos = [];
  widgetDtos.forEach((widgetDto) => {
    Object.values(widgetDto.mentorsControlMapDto ?? {}).forEach(
      (widgetMentorCtrlDto) => {
        if (
          widgetMentorCtrlDto.extendedMentorDto.mentorDto.mentorOwnerId ===
          userId
        ) {
          widgetAndExMentorDtos.push({
            widgetDto,
            extendedMentorDto: widgetMentorCtrlDto.extendedMentorDto,
          });
        }
      },
    );
  });
  return widgetAndExMentorDtos;
};

service.getAuthorizedSelectedMentorIds = (
  selectedMentorDtos,
  currentUserId,
) => {
  // if currentUserId is passed then team mentor ids will also be returned
  let mentorIds = [];
  for (const mentorId in selectedMentorDtos) {
    const extendedMentorDto =
      selectedMentorDtos[mentorId].mentorsControlMapDto[mentorId]
        ?.extendedMentorDto;
    if (
      extendedMentorDto &&
      extendedMentorDto.mentorDto.mentorOwnerType ===
        VIDABOO_CONSTANTS.MENTOR_OWNER_TYPE.PERSONAL &&
      extendedMentorDto.privateDataConsentStatus ===
        VIDABOO_CONSTANTS.PRIVATE_DATA_ACCESS_STATES.GRANTED
    ) {
      mentorIds.push(mentorId);
    }
  }
  if (currentUserId) {
    const authorizedTeamMentorIds = service.getAuthorizedSelectedTeamMentorIds(
      mentorIds,
      selectedMentorDtos,
      currentUserId,
    );
    mentorIds = [...mentorIds, ...authorizedTeamMentorIds];
  }
  return mentorIds;
};

service.getAuthorizedSelectedTeamMentorIds = (
  existingMentorIds,
  selectedMentorDtos,
  currentUserId,
) => {
  // some of the mentors we have access to are not "readable". For example, an account manager can have access to configure mentors but doesn't necessarily have access to read chat information.
  let mentorIds = Object.keys(selectedMentorDtos ?? {}).filter((mentorId) => {
    const widgetDto = selectedMentorDtos[mentorId];
    const teamExMentorDto =
      selectedMentorDtos[mentorId].teamMentorsMapDto[mentorId]
        ?.extendedMentorDto;
    const currentUserRoleInWidget = service.getWidgetAdminRole(
      widgetDto,
      currentUserId,
    );
    return (
      teamExMentorDto?.mentorDto?.mentorOwnerType ===
        VIDABOO_CONSTANTS.MENTOR_OWNER_TYPE.WIDGET && // teamExMentorDto needs to exist and be of type WIDGET.
      !existingMentorIds.includes(mentorId) && // not already added to the list
      [
        VIDABOO_CONSTANTS.WIDGET_INTERACTION_MODE.TEAM_WIDGET,
        VIDABOO_CONSTANTS.WIDGET_INTERACTION_MODE.LIST_AND_CHAT,
      ].includes(widgetDto.widgetAppCtrlDto.widgetInteractionMode) &&
      (widgetDto.widgetAppCtrlDto.widgetPrivacyMode ===
        VIDABOO_CONSTANTS.WIDGET_PRIVACY_MODES.FULLY_SHARED ||
        (widgetDto.widgetAppCtrlDto.widgetPrivacyMode ===
          VIDABOO_CONSTANTS.WIDGET_PRIVACY_MODES.HALF_PRIVATE &&
          [
            VIDABOO_CONSTANTS.WIDGET_ADMIN_ROLES.ADMIN,
            VIDABOO_CONSTANTS.WIDGET_ADMIN_ROLES.READ_PRIVATE,
          ].includes(currentUserRoleInWidget)))
    );
  });
  return mentorIds;
};

service.getBackOfficeConfiguration = (
  isAccountManager,
  currentUser,
  widgetDtos,
  selectedExMentorDto, // selectedExMentorDto will be passed only in case of expert settings (when we want to have TEAM_ADMIN, FIRST_RESPONDER configuration as well)
) => {
  const filteredWidgetDtos = service.getFilteredWidgetDtosByRoleAndPrivacyMode(
    isAccountManager,
    currentUser,
    widgetDtos,
  );
  const isMultiMode =
    filteredWidgetDtos?.some(
      (widgetDto) =>
        Object.keys(widgetDto.mentorsControlMapDto).length +
          Object.keys(widgetDto.teamMentorsMapDto).length >
        1,
    ) || filteredWidgetDtos?.length > 1;

  let backOfficeConfiguration = isAccountManager
    ? isMultiMode
      ? VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.ADMIN_MULTI_MODE
      : VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.ADMIN_SINGLE_MODE
    : isMultiMode
      ? VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.NON_ADMIN_MULTI_MODE
      : filteredWidgetDtos?.length === 0
        ? VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.NON_ADMIN_EMPTY_WIDGET
        : VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.NON_ADMIN_SINGLE_MODE;

  if (
    [
      VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.NON_ADMIN_SINGLE_MODE,
      VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.NON_ADMIN_MULTI_MODE,
    ].includes(backOfficeConfiguration)
  ) {
    let isTeamAdmin = false,
      isFirstResponder = false;
    if (selectedExMentorDto) {
      const widgetDto = service.getWidgetDtoExMentorDtoByTeamMentorIdOrMentorId(
        widgetDtos,
        selectedExMentorDto?.mentorDto?.mentorId,
      )?.widgetDto;
      const myRoleInWidget = service.getWidgetAdminRole(
        widgetDto,
        currentUser.userId,
      );
      isTeamAdmin =
        myRoleInWidget === VIDABOO_CONSTANTS.WIDGET_ADMIN_ROLES.ADMIN;
      isFirstResponder =
        myRoleInWidget === VIDABOO_CONSTANTS.WIDGET_ADMIN_ROLES.FIRST_RESPONDER;
    }
    if (isTeamAdmin) {
      backOfficeConfiguration = isMultiMode
        ? VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.TEAM_ADMIN_MULTI_MODE
        : VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.TEAM_ADMIN_SINGLE_MODE;
    } else if (isFirstResponder) {
      backOfficeConfiguration = isMultiMode
        ? VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.FIRST_RESPONDER_MULTI_MODE
        : VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION
            .FIRST_RESPONDER_SINGLE_MODE;
    }
  }
  return backOfficeConfiguration;
};

service.getChatParticipantsInfo = ({
  roomDto = {},
  chatRooms = [],
  widgetDtos = [],
  authorizedMentorIds = [],
  currentUserId,
  needChatRoomInfo = false,
  cExtendedMentorDto = null, // provided only from customer side
}) => {
  // final return obj will look like :
  // {
  //   mentor_id: {
  //     chatRoom : undefined,
  //     extendedMentorDto : {}, // extendedMentorDto from selected widget
  //     isMe : true, // true if mentorOwnerId is equal to currentUserId
  //     participantName : "",
  //     widgetName:""
  //   },
  //   guest_id : {
  //     chatRoom : {}, // chatRoom with MENTOR as OWNER (1 X 1)
  //     participantName : "",
  //   }
  // };

  // todo 1720 : fix the addition of OONI
  let participantsInfo = {};
  const {participants, ownerObjectId} = roomDto || {};

  const isTeamChatRoom =
    roomDto.ownerObjectType ===
    VIDABOO_CONSTANTS.CHAT_ROOM_OWNER_TYPE.TEAM_MENTOR;

  if (isTeamChatRoom) {
    const widgetDto = widgetDtos?.find(
      (widgetDto) => !!widgetDto.teamMentorsMapDto[ownerObjectId],
    );

    const widgetName = widgetDto.widgetAppCtrlDto.name;

    // Add mentors
    Object.values(widgetDto.mentorsControlMapDto).forEach(
      ({extendedMentorDto}) => {
        const {mentorDto} = extendedMentorDto;
        const isMe = mentorDto.mentorOwnerId === currentUserId;
        const participantName = mentorDto.displayName;

        const chatRoomWithPeer = findPeerRoomInfo(
          mentorDto.mentorId,
          chatRooms,
          authorizedMentorIds,
        );

        participantsInfo[mentorDto.mentorId] = {
          isMe,
          chatRoom: needChatRoomInfo ? chatRoomWithPeer : null,
          extendedMentorDto,
          participantName,
          widgetName,
        };
      },
    );

    // Add OONI_AI and guest participants
    let totalGuests = 0;
    participants.forEach((participant) => {
      const {objectId, objectType} = participant;

      if (!participantsInfo[objectId]) {
        if (objectType === VIDABOO_CONSTANTS.CHAT_PARTICIPANT_TYPES.GUEST) {
          const chatRoomWithPeer = findPeerRoomInfo(
            objectId,
            chatRooms,
            authorizedMentorIds,
          );
          const {peerEmail, peerName} =
            chatRoomWithPeer?.chatRoomPeerInfoDto || {};
          participantsInfo[objectId] = {
            participantName: peerEmail || peerName || 'Guest ' + ++totalGuests,
            chatRoom: needChatRoomInfo ? chatRoomWithPeer : null,
          };
        } else if (
          objectType === VIDABOO_CONSTANTS.CHAT_PARTICIPANT_TYPES.OONI_AI
        ) {
          const teamMentorDto = widgetDto.teamMentorsMapDto[ownerObjectId];
          const assistantName = teamMentorDto?.appControlDto?.assistantName;
          participantsInfo[VIDABOO_CONSTANTS.CHAT_PARTICIPANT_TYPES.OONI_AI] = {
            isAiBot: true,
            participantName: utilsService.getAssistantName(assistantName),
            widgetName,
          };
        }
      }
    });
  } else {
    const selectedMentorId = ownerObjectId;
    let totalGuests = 0;
    participants.forEach((participant) => {
      const {objectId} = participant;

      if (participant.objectId === selectedMentorId) {
        let {widgetName, extendedMentorDto} =
          service.getWidgetNameExMentorDtoUsingMentorId(
            widgetDtos,
            selectedMentorId,
          );
        // case :
        // 1. customer-1 starts the call from widget-1
        // 2. mentor-1 accepts the call
        // 3. now customer-2, who is on personal page of mentor-1 whose widget is totally different (say widget-2) requests to join the call
        // 4. mentor accepts the call
        // 5. after this point, the group chat room will be created whose ownerObjectId of mentor will be based on widget-1
        // 6. now the problem is customer-2 doesn't have access to widget-1 (as he is on personal page of widget-2)
        // 7. So we need to find the extendedMentorDto based on widget-2

        if (!extendedMentorDto && cExtendedMentorDto) {
          ({widgetName, extendedMentorDto} =
            service.getWidgetNameExMentorDtoUsingMentorId(
              widgetDtos,
              cExtendedMentorDto.mentorDto.mentorId,
            ));
        }
        const isMe =
          extendedMentorDto?.mentorDto?.mentorOwnerId === currentUserId;
        const participantName = extendedMentorDto?.mentorDto?.displayName;

        participantsInfo[objectId] = {
          isMe,
          extendedMentorDto,
          participantName,
          widgetName,
        };
      } else {
        const chatRoomWithPeer = findPeerRoomInfo(
          objectId,
          chatRooms,
          authorizedMentorIds,
        );
        const {peerEmail, peerName} =
          chatRoomWithPeer?.chatRoomPeerInfoDto || {};
        participantsInfo[objectId] = {
          participantName: peerName || peerEmail || 'Guest ' + ++totalGuests,
          chatRoom: needChatRoomInfo ? chatRoomWithPeer : null,
        };
      }
    });
  }

  return participantsInfo;
};

service.getControlledFeaturesAsBackOffice = (controlledFeaturesDto, key) => {
  if (key) {
    return controlledFeaturesDto?.backOffice[key];
  }
  return controlledFeaturesDto?.backOffice;
};

service.getFilteredWidgetDtosByRoleAndPrivacyMode = (
  isAccountManager = false,
  currentUser = '',
  widgetDtos = [],
) => {
  let filteredWidgetDtos = [];
  widgetDtos?.forEach((widgetDto) => {
    // Filter mentorsControlMapDto to include only PERSONAL mentors owned by current user
    const filteredMentorsControlMapDto = {};
    let currentUserIsPartOfWidget = false;
    const myRoleInWidget = service.getWidgetAdminRole(
      widgetDto,
      currentUser.userId,
    );

    Object.keys(widgetDto.mentorsControlMapDto ?? {}).forEach((mentorId) => {
      const mentorCtrlMapDto = widgetDto.mentorsControlMapDto[mentorId];
      const extendedMentorDto = mentorCtrlMapDto?.extendedMentorDto;
      if (
        isAccountManager ||
        myRoleInWidget === VIDABOO_CONSTANTS.WIDGET_ADMIN_ROLES.ADMIN ||
        (extendedMentorDto.mentorDto.mentorOwnerType ===
          VIDABOO_CONSTANTS.MENTOR_OWNER_TYPE.PERSONAL &&
          extendedMentorDto.mentorDto.mentorOwnerId === currentUser.userId)
      ) {
        currentUserIsPartOfWidget = true;
        filteredMentorsControlMapDto[mentorId] = mentorCtrlMapDto;
      }
    });
    // Team mentor access is provided as follows:
    // 1. in Fully shared mode, everyone in the widget can access.
    // 2. in half private mode, only admins and 1st responders have access to team-level conversations.
    const filteredTeamMentorsMapDto =
      isAccountManager ||
      (currentUserIsPartOfWidget &&
        widgetDto.widgetAppCtrlDto.widgetPrivacyMode ===
          VIDABOO_CONSTANTS.WIDGET_PRIVACY_MODES.FULLY_SHARED) ||
      ([
        VIDABOO_CONSTANTS.WIDGET_ADMIN_ROLES.ADMIN,
        VIDABOO_CONSTANTS.WIDGET_ADMIN_ROLES.READ_PRIVATE,
      ].includes(myRoleInWidget) &&
        widgetDto.widgetAppCtrlDto.widgetPrivacyMode ===
          VIDABOO_CONSTANTS.WIDGET_PRIVACY_MODES.HALF_PRIVATE)
        ? {...(widgetDto.teamMentorsMapDto ?? {})}
        : {};

    // Only include the widget if it has at least one filtered mentor or team mentor
    if (
      Object.keys(filteredMentorsControlMapDto).length > 0 ||
      Object.keys(filteredTeamMentorsMapDto).length > 0
    ) {
      filteredWidgetDtos.push({
        ...widgetDto,
        mentorsControlMapDto: filteredMentorsControlMapDto,
        teamMentorsMapDto: filteredTeamMentorsMapDto,
      });
    }
  });
  return filteredWidgetDtos;
};

service.getFirstMentorDtoByWidgetAndUserId = (widgetDtos, widgetId, userId) => {
  const widgetDto = service.getWidgetDtoByWidgetId(widgetDtos, widgetId);

  return Object.values(widgetDto?.mentorsControlMapDto ?? {}).find(
    (widgetMentorCtrlDto) =>
      widgetMentorCtrlDto.extendedMentorDto.mentorDto.mentorOwnerId === userId,
  );
};

service.getFirstSelectedMentorDtoByWidgetAndUserId = (
  widgetDtos,
  selectedMentorDtos,
  widgetId,
  userId,
) => {
  const widgetDto = service.getWidgetDtoByWidgetId(widgetDtos, widgetId);

  return Object.values(widgetDto?.mentorsControlMapDto ?? {}).find(
    (widgetMentorCtrlDto) =>
      widgetMentorCtrlDto.extendedMentorDto.mentorDto.mentorOwnerId ===
        userId &&
      selectedMentorDtos[
        widgetMentorCtrlDto.extendedMentorDto.mentorDto.mentorId
      ],
  );
};

service.getFirstSelectedWidgetMentorCtrlDtoUsingUserId = (
  selectedMentorDtos,
  userId,
) => {
  let widgetMentorCtrlDto = null;
  for (const mentorId in selectedMentorDtos ?? {}) {
    if (
      !widgetMentorCtrlDto &&
      selectedMentorDtos[mentorId].mentorsControlMapDto[mentorId]
        ?.extendedMentorDto.mentorDto.mentorOwnerId === userId
    ) {
      widgetMentorCtrlDto =
        selectedMentorDtos[mentorId].mentorsControlMapDto[mentorId];
    }
  }
  return widgetMentorCtrlDto;
};

service.getFirstWidgetDtoAndMentorIdUsingCurrentUserId = (
  currentUserId = '',
  widgetDtos = [],
) => {
  let result = {};
  for (let i = 0; i < widgetDtos.length; i++) {
    const primaryWidgetMentorCtrlDto = Object.values(
      widgetDtos[i].mentorsControlMapDto ?? {},
    ).find(
      (widgetMentorCtrlDto) =>
        widgetMentorCtrlDto.extendedMentorDto.mentorDto.mentorOwnerId ===
        currentUserId,
    );
    if (primaryWidgetMentorCtrlDto) {
      result.mentorId =
        primaryWidgetMentorCtrlDto.extendedMentorDto.mentorDto.mentorId;
      result.widgetDto = widgetDtos[i];
      break;
    }
  }
  return result;
};

service.getFormattedWidgetName = (widgetName = '') => {
  let name = null;
  const nameWithoutSpaces = widgetName?.replace(/ +/g, ' ')?.split(' ');
  const firstName = nameWithoutSpaces[0];
  const lastName = nameWithoutSpaces[1];
  if (firstName && lastName) {
    name = firstName[0] + lastName[0];
  } else if (firstName) {
    name = firstName?.slice(0, 2);
  }
  return name;
};

service.getIsBetaFeatureEnabled = (enabledBetaFeatures, betaFeature) => {
  return enabledBetaFeatures?.includes(betaFeature);
};

service.getIsOoniEnabledForMentor = (selectedWidgetDto, mentorId) => {
  const mentorsMapDto = expertHomeService.getMentorsMapDto(
    selectedWidgetDto,
    mentorId,
  );
  const extendedMentorDto = mentorsMapDto?.[mentorId]?.extendedMentorDto;

  const isOoniEnabledForWidget =
    selectedWidgetDto?.widgetAppCtrlDto?.isOoniEnabled;
  const isOoniEnabledForMentor =
    extendedMentorDto?.mentorDto?.appControlDto?.isOoniEnabled;
  return isOoniEnabledForWidget && isOoniEnabledForMentor;
};

service.getIsSingleWidgetSelected = (widgetDto) => {
  return Object.keys(widgetDto?.mentorsControlMapDto ?? {}).length === 1;
};

service.getIsTeamMentorDtoSelected = (selectedMentorDtos) => {
  const teamMentorId = Object.keys(selectedMentorDtos || {})[0];
  return !!(
    selectedMentorDtos &&
    teamMentorId &&
    selectedMentorDtos[teamMentorId]?.teamMentorsMapDto[teamMentorId]
  );
};

service.getIsWidgetAllMentorsSelected = (widgetDto, selectedMentorDtos) => {
  let IsWidgetAllMentorsSelected = false;
  const currentWidgetAllMentorIds = Object.keys({
    ...widgetDto.mentorsControlMapDto,
    ...widgetDto.teamMentorsMapDto,
  });
  const selectedMentorIds = Object.keys(selectedMentorDtos);
  if (currentWidgetAllMentorIds.length && selectedMentorIds.length) {
    IsWidgetAllMentorsSelected = currentWidgetAllMentorIds.every((mentorId) =>
      selectedMentorIds.includes(mentorId),
    );
  }
  return IsWidgetAllMentorsSelected;
};

service.getIsWidgetBrainAccessible = (backOfficeConfiguration) => {
  return [
    VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.ADMIN_SINGLE_MODE,
    VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.ADMIN_MULTI_MODE,
    VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.TEAM_ADMIN_SINGLE_MODE,
    VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.TEAM_ADMIN_MULTI_MODE,
  ].includes(backOfficeConfiguration);
};

service.getKbTrainingStatus = (
  newKbTrainingStatus,
  kbResourceDtos,
  brainDto,
) => {
  let kbTrainingStatus;
  const pendingTraining = kbResourceDtos?.some((kbResourceDto) =>
    [
      VIDABOO_CONSTANTS.KBR_STATUSES.INIT,
      VIDABOO_CONSTANTS.KBR_STATUSES.PENDING_DELETION,
      VIDABOO_CONSTANTS.KBR_STATUSES.PENDING_TRAINING,
      VIDABOO_CONSTANTS.KBR_STATUSES.UPLOADING,
    ].includes(kbResourceDto.status),
  );
  kbTrainingStatus =
    newKbTrainingStatus === 'ACTIVE'
      ? VIDABOO_CONSTANTS.KB_TRAINING_STATUSES.TRAINING_FLOW_ACTIVE
      : pendingTraining ||
          newKbTrainingStatus === 'ERROR' ||
          brainDto?.embeddingModel?.pendingTrainingModel
        ? VIDABOO_CONSTANTS.KB_TRAINING_STATUSES.PENDING_TRAINING
        : VIDABOO_CONSTANTS.KB_TRAINING_STATUSES.TRAINED;
  return kbTrainingStatus;
};
service.getKbTrainingStatusFromExistingKBResourceDtosAndBrain = (
  kbResourceDtos,
  brainDto,
) => {
  let kbTrainingStatus;
  const kbResourcesPendingTraining = kbResourceDtos?.some((kbResourceDto) =>
    [
      VIDABOO_CONSTANTS.KBR_STATUSES.INIT,
      VIDABOO_CONSTANTS.KBR_STATUSES.PENDING_DELETION,
      VIDABOO_CONSTANTS.KBR_STATUSES.PENDING_TRAINING,
      VIDABOO_CONSTANTS.KBR_STATUSES.UPLOADING,
    ].includes(kbResourceDto.status),
  );
  kbTrainingStatus =
    kbResourcesPendingTraining || brainDto?.embeddingModel?.pendingTrainingModel
      ? VIDABOO_CONSTANTS.KB_TRAINING_STATUSES.PENDING_TRAINING
      : VIDABOO_CONSTANTS.KB_TRAINING_STATUSES.TRAINED;
  return kbTrainingStatus;
};

service.getMentorAccessRights = (mentorAccessRightType, data) => {
  switch (mentorAccessRightType) {
    case VIDABOO_CONSTANTS.MENTOR_ACCESS_RIGHT_TYPES.CHAT_ROOM:
      return service.getMentorChatAccessRights(data);
    case VIDABOO_CONSTANTS.MENTOR_ACCESS_RIGHT_TYPES.PROFILE_SECTION:
      return service.getMentorProfileSectionAccessRights(data);
    default:
      console.debug(
        'No access right found for feature: ',
        mentorAccessRightType,
      );
  }
};

service.getMentorChatAccessRights = ({
  chatRoom,
  participantId,
  currentUserId,
  isAccountManager,
  widgetDtos,
  users,
}) => {
  let mentorChatAccessRights = {read: false, write: false};
  let widgetDto, roomOwnerExMentorDto;

  const selectedMentorId = chatRoom.ownerObjectId;

  if (
    chatRoom.ownerObjectType === VIDABOO_CONSTANTS.CHAT_ROOM_OWNER_TYPE.MENTOR
  ) {
    const widgetExMentorDtoObj = service.getWidgetDtoExMentorDtoUsingMentorId(
      widgetDtos,
      selectedMentorId,
    );
    widgetDto = widgetExMentorDtoObj.widgetDto;
    roomOwnerExMentorDto = widgetExMentorDtoObj.extendedMentorDto;
  } else {
    const widgetExMentorDtoObj =
      service.getWidgetDtoExMentorDtoUsingTeamMentorId(
        widgetDtos,
        selectedMentorId,
      );
    widgetDto = widgetExMentorDtoObj.widgetDto;
    roomOwnerExMentorDto = widgetExMentorDtoObj.extendedMentorDto;
  }

  const myRole = service.getWidgetAdminRole(widgetDto, currentUserId);
  const isAdminOrFirstResponder = [
    VIDABOO_CONSTANTS.WIDGET_ADMIN_ROLES.ADMIN,
    VIDABOO_CONSTANTS.WIDGET_ADMIN_ROLES.READ_PRIVATE,
  ].includes(myRole);
  const usersDataPermissionStatus =
    expertHomeService.getUsersDataPermissionStatus(users, currentUserId);

  if (
    chatRoom.ownerObjectType === VIDABOO_CONSTANTS.CHAT_ROOM_OWNER_TYPE.MENTOR
  ) {
    const doesSelectedMentorBelongToCurrentUser =
      roomOwnerExMentorDto.mentorDto.mentorOwnerId === currentUserId;
    mentorChatAccessRights.write = doesSelectedMentorBelongToCurrentUser;
    const privateDataConsentStatus =
      usersDataPermissionStatus[roomOwnerExMentorDto.mentorDto.mentorOwnerId];
    mentorChatAccessRights.read =
      mentorChatAccessRights.write ||
      (isAccountManager &&
        privateDataConsentStatus ===
          VIDABOO_CONSTANTS.PRIVATE_DATA_ACCESS_STATES.GRANTED);
  } else {
    if (
      widgetDto.widgetAppCtrlDto.widgetPrivacyMode ===
      VIDABOO_CONSTANTS.WIDGET_PRIVACY_MODES.FULLY_SHARED
    ) {
      const widgetTeamWriteMode =
        widgetDto.widgetAppCtrlDto.widgetTeamWriteMode;
      mentorChatAccessRights.write = chatRoom.taskOwnerMentorId
        ? widgetTeamWriteMode ===
          VIDABOO_CONSTANTS.WIDGET_TEAM_WRITE_MODES.TASK_OWNER_WRITE
          ? chatRoom.taskOwnerMentorId === participantId
          : true
        : true;
      mentorChatAccessRights.read = true;
    } else if (
      widgetDto.widgetAppCtrlDto.widgetPrivacyMode ===
      VIDABOO_CONSTANTS.WIDGET_PRIVACY_MODES.HALF_PRIVATE
    ) {
      if (chatRoom.taskOwnerMentorId) {
        mentorChatAccessRights.write =
          chatRoom.taskOwnerMentorId === participantId &&
          isAdminOrFirstResponder;
        mentorChatAccessRights.read = isAdminOrFirstResponder;
      } else {
        mentorChatAccessRights.write = isAdminOrFirstResponder;
        mentorChatAccessRights.read = isAdminOrFirstResponder;
      }
    } else if (
      widgetDto.widgetAppCtrlDto.widgetPrivacyMode ===
      VIDABOO_CONSTANTS.WIDGET_PRIVACY_MODES.FULLY_PRIVATE
    ) {
      alert("Private room can't be owned by team mentor");
    }
  }
  return mentorChatAccessRights;
};

service.getMentorIdUsingChatRoomId = (roomId = '', chatRooms = []) => {
  return chatRooms.find((chatRoomDto) => chatRoomDto.roomId === roomId)
    ?.ownerObjectId;
};

service.getMentorPillColor = (serialNum) => {
  const hue = (serialNum * 137.5) % 360;
  const saturation = 100;
  const lightness = 35;
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

service.getMentorPillName = (extendedMentorDto = {}) => {
  let mentorPillName = '';

  if (
    extendedMentorDto.mentorDto.mentorOwnerType ===
    VIDABOO_CONSTANTS.MENTOR_OWNER_TYPE.WIDGET
  ) {
    return (
      extendedMentorDto.mentorDto.profileName?.toUpperCase()?.slice(0, 2) ??
      'TM'
    );
  }

  const firstName = extendedMentorDto.firstName;
  const lastName = extendedMentorDto.lastName;

  if (firstName && lastName) {
    mentorPillName = firstName[0] + lastName[0];
  } else if (firstName) {
    mentorPillName = firstName[0];
  } else {
    console.debug(extendedMentorDto);
  }
  return mentorPillName.toUpperCase();
};

service.getMentorsMapDto = (widgetDto, mentorOrTeamMentorId) => {
  // this function will return mentorsControlMapDto if mentorOrTeamMentorId is mentorId otherwise will return teamMentorsMapDto
  return widgetDto?.mentorsControlMapDto?.[mentorOrTeamMentorId]
    ? widgetDto.mentorsControlMapDto
    : widgetDto?.teamMentorsMapDto;
};

service.getMentorProfileSectionAccessRights = ({
  chatRoom,
  widgetDtos,
  currentUserId,
  isAccountManager,
}) => {
  // logic based on https://consolto.atlassian.net/browse/CFW-1885
  let mentorProfileSectionAccessRights = {read: false, write: false};
  let widgetDto, selectedExMentorDto;

  const selectedMentorId = chatRoom.ownerObjectId;

  if (
    chatRoom.ownerObjectType === VIDABOO_CONSTANTS.CHAT_ROOM_OWNER_TYPE.MENTOR
  ) {
    const widgetExMentorDtoObj = service.getWidgetDtoExMentorDtoUsingMentorId(
      widgetDtos,
      selectedMentorId,
    );
    widgetDto = widgetExMentorDtoObj.widgetDto;
    selectedExMentorDto = widgetExMentorDtoObj.extendedMentorDto;
  } else {
    const widgetExMentorDtoObj =
      service.getWidgetDtoExMentorDtoUsingTeamMentorId(
        widgetDtos,
        selectedMentorId,
      );
    widgetDto = widgetExMentorDtoObj.widgetDto;
    selectedExMentorDto = widgetExMentorDtoObj.extendedMentorDto;
  }

  const doesSelectedMentorBelongToCurrentUser =
    selectedExMentorDto.mentorDto.mentorOwnerId === currentUserId;

  if (
    chatRoom.ownerObjectType === VIDABOO_CONSTANTS.CHAT_ROOM_OWNER_TYPE.MENTOR
  ) {
    mentorProfileSectionAccessRights.write =
      doesSelectedMentorBelongToCurrentUser ||
      (isAccountManager &&
        selectedExMentorDto.privateDataConsentStatus ===
          VIDABOO_CONSTANTS.PRIVATE_DATA_ACCESS_STATES.GRANTED);
    mentorProfileSectionAccessRights.read =
      mentorProfileSectionAccessRights.write;
  } else {
    const myRole = service.getWidgetAdminRole(widgetDto, currentUserId);
    mentorProfileSectionAccessRights.write =
      isAccountManager || myRole === VIDABOO_CONSTANTS.WIDGET_ADMIN_ROLES.ADMIN;
    mentorProfileSectionAccessRights.read =
      mentorProfileSectionAccessRights.write ||
      myRole === VIDABOO_CONSTANTS.WIDGET_ADMIN_ROLES.READ_PRIVATE;
  }

  return mentorProfileSectionAccessRights;
};

service.getMetaDataFromChatMessageDto = (chatMessageDto) => {
  try {
    if (chatMessageDto?.metaData) {
      return JSON.parse(chatMessageDto.metaData);
    }
  } catch (e) {
    console.debug('Failed to parse metaData: ', chatMessageDto?.metaData);
  }
  return {};
};

service.getOoniChatHistoryMsgRole = (type) => {
  if (!type) return 'user';
  switch (type) {
    case VIDABOO_CONSTANTS.CHAT_PARTICIPANT_TYPES.MENTOR:
    case VIDABOO_CONSTANTS.CHAT_PARTICIPANT_TYPES.GUEST:
      return 'user';
    case VIDABOO_CONSTANTS.CHAT_PARTICIPANT_TYPES.OONI_AI:
    case VIDABOO_CONSTANTS.CHAT_PARTICIPANT_TYPES.EXTERNAL_API:
      return 'assistant';
    case VIDABOO_CONSTANTS.CHAT_PARTICIPANT_TYPES.ACCOUNT:
    case VIDABOO_CONSTANTS.CHAT_PARTICIPANT_TYPES.WIDGET:
      return 'system';
    default:
      return 'user';
  }
};

service.getOoniChatHistoryStr = (messages = []) => {
  let formatMessagesToChatJson = messages
    .filter(
      (msgObj) => !msgObj.isDefaultDemoMsg && msgObj.messageText?.trim() !== '',
    )
    .sort((a, b) => {
      // sort by sendTime
      const aTime = a.sendTime ? new Date(a.sendTime) : null;
      const bTime = b.sendTime ? new Date(b.sendTime) : null;
      if (aTime && bTime) return aTime - bTime;
      if (aTime) return -1;
      if (bTime) return 1;
      return 0;
    })
    .map((msgObj) => {
      const role = service.getOoniChatHistoryMsgRole(msgObj.senderObjectType);
      const content = msgObj.messageText;
      let name = msgObj.senderObjectType;
      // if (msgObj.senderObjectId) {
      //   const senderObjectId = msgObj.senderObjectId;
      //   name =
      //     senderObjectId.length > 8
      //       ? senderObjectId.slice(0, 4) + senderObjectId.slice(-4)
      //       : senderObjectId;
      // }
      return {role, name, content};
    });
  try {
    return formatMessagesToChatJson
      .map((obj) => JSON.stringify(obj))
      .join('\n');
  } catch (e) {
    console.debug('Error stringifying chat history:', e);
    return '';
  }
};

service.getOoniAiAction = (chatMessageDto) => {
  let ooniAiAction = VIDABOO_CONSTANTS.OONI_AI_ACTIONS.COMPLETION_PENDING;
  const metaData = service.getMetaDataFromChatMessageDto(chatMessageDto);
  if (metaData.ooniAction) {
    ooniAiAction = metaData.ooniAction;
  }
  return ooniAiAction;
};

service.getSelectedMentorDto = (selectedMentorDtos) => {
  const mentorId = Object.keys(selectedMentorDtos || {})[0];
  if (mentorId) {
    return (
      selectedMentorDtos[mentorId]?.mentorsControlMapDto[mentorId]
        ?.extendedMentorDto?.mentorDto ??
      selectedMentorDtos[mentorId]?.teamMentorsMapDto[mentorId]
        ?.extendedMentorDto?.mentorDto
    );
  } else {
    return null;
  }
};

// service.getSelectedTeamMentorDto = (selectedTeamMentorDtos) => {
//   const teamMentorId = Object.keys(selectedTeamMentorDtos ?? {})[0];
//   if (teamMentorId) {
//     return selectedTeamMentorDtos[teamMentorId]?.teamMentorsMapDto[teamMentorId]
//       ?.extendedMentorDto?.mentorDto;
//   } else {
//     return null;
//   }
// };

// service.getSelectedTeamWidgetMentorCtrlDto = (selectedTeamMentorDtos = {}) => {
//   const teamMentorId = Object.keys(selectedTeamMentorDtos || {})[0];
//   if (teamMentorId) {
//     return selectedTeamMentorDtos[teamMentorId].teamMentorsMapDto[teamMentorId];
//   } else {
//     return null;
//   }
// };

service.getSelectedWidgetDto = (selectedMentorDtos) => {
  return Object.values(selectedMentorDtos || {})[0];
};

service.getSelectedWidgetMentorCtrlDto = (selectedMentorDtos = {}) => {
  const mentorId = Object.keys(selectedMentorDtos || {})[0];
  if (mentorId) {
    return (
      selectedMentorDtos[mentorId].mentorsControlMapDto[mentorId] ??
      selectedMentorDtos[mentorId].teamMentorsMapDto[mentorId]
    );
  } else {
    return null;
  }
};

service.getSelectedWidgetMentorCtrlDtosByType = (
  selectedMentorDtos = {},
  mentorOwnerType,
) => {
  let selectedWidgetMentorCtrlDtos = {};
  for (let [mentorId] of Object.entries(selectedMentorDtos ?? {})) {
    const mentorsMapDto = expertHomeService.getMentorsMapDto(
      selectedMentorDtos[mentorId],
      mentorId,
    );
    const extendedMentorDto = mentorsMapDto[mentorId]?.extendedMentorDto;
    if (extendedMentorDto.mentorDto.mentorOwnerType === mentorOwnerType) {
      selectedWidgetMentorCtrlDtos[mentorId] = mentorsMapDto[mentorId];
    }
  }
  return selectedWidgetMentorCtrlDtos;
};

service.getUniqueWidgetDtosWithIds = (selectedMentorDtos) => {
  // input: {mentorId1: widgetDto1, mentorId2: widgetDto1, mentorId3: widgetDto2 };
  // output: {widgetId1: widgetDto1, widgetId2: widgetDto2};
  return Object.values(selectedMentorDtos || {}).reduce(
    (accumulatedSelectedWidgetDtos, widgetDto) => {
      accumulatedSelectedWidgetDtos[widgetDto.widgetId] = widgetDto;
      return accumulatedSelectedWidgetDtos;
    },
    {},
  );
};

service.getUsersDataPermissionStatus = (users, currentUserId) => {
  let result = {};
  for (const userId in users) {
    const privateDataConsent = users[userId].privateDataConsent;
    if (privateDataConsent?.state || userId === currentUserId) {
      result[userId] = VIDABOO_CONSTANTS.PRIVATE_DATA_ACCESS_STATES.GRANTED;
    } else {
      if (
        (privateDataConsent?.requestDate &&
          privateDataConsent?.changeDate &&
          moment(privateDataConsent.changeDate).isBefore(
            moment(privateDataConsent.requestDate),
          )) ||
        (privateDataConsent?.requestDate && !privateDataConsent?.changeDate)
      ) {
        result[userId] = VIDABOO_CONSTANTS.PRIVATE_DATA_ACCESS_STATES.PENDING;
      } else {
        result[userId] = VIDABOO_CONSTANTS.PRIVATE_DATA_ACCESS_STATES.REQUEST;
      }
    }
  }
  return result;
};

service.getUnAuthorizedUserDtos = (selectedMentorDtos, usersCtrlMap) => {
  let userDtos = [];
  for (const mentorId in selectedMentorDtos || {}) {
    const mentorsMapDto = expertHomeService.getMentorsMapDto(
      selectedMentorDtos[mentorId],
      mentorId,
    );
    const extendedMentorDto = mentorsMapDto[mentorId]?.extendedMentorDto;
    if (
      extendedMentorDto.mentorDto.mentorOwnerType !==
        VIDABOO_CONSTANTS.MENTOR_OWNER_TYPE.WIDGET &&
      extendedMentorDto.privateDataConsentStatus !==
        VIDABOO_CONSTANTS.PRIVATE_DATA_ACCESS_STATES.GRANTED
    ) {
      const userDto =
        usersCtrlMap &&
        usersCtrlMap[extendedMentorDto.mentorDto.mentorOwnerId]?.userDto;
      if (userDto && !userDtos.includes(userDto)) {
        userDtos.push(userDto);
      }
    }
  }
  return userDtos;
};

service.getWidgetAdminRole = (widgetDto, userId) => {
  return (
    widgetDto?.widgetAdminDtos?.find(
      (adminDto) =>
        adminDto.objectType === VIDABOO_CONSTANTS.MENTOR_OWNER_TYPE.PERSONAL &&
        adminDto.objectId === userId,
    )?.role ?? VIDABOO_CONSTANTS.WIDGET_ADMIN_ROLES.NONE
  );
};

service.getWidgetDtoByMentorId = (widgetDtos, mentorId) => {
  let result = {};
  widgetDtos?.forEach((widgetDto) => {
    if (widgetDto.mentorsControlMapDto[mentorId]) {
      result = widgetDto;
    }
  });
  return result;
};

service.getWidgetDtoByTeamMentorId = (widgetDtos, teamMentorId) => {
  let result = {};
  widgetDtos?.forEach((widgetDto) => {
    if (widgetDto.teamMentorsMapDto[teamMentorId]) {
      result = widgetDto;
    }
  });
  return result;
};

service.getWidgetDtoExMentorDtoByTeamMentorIdOrMentorId = (
  widgetDtos,
  teamMentorIdOrMentorId,
) => {
  let widgetDtoExMentorDto = service.getWidgetDtoExMentorDtoUsingMentorId(
    widgetDtos,
    teamMentorIdOrMentorId,
  );
  if (
    !widgetDtoExMentorDto.widgetDto ||
    !widgetDtoExMentorDto.extendedMentorDto
  ) {
    widgetDtoExMentorDto = service.getWidgetDtoExMentorDtoUsingTeamMentorId(
      widgetDtos,
      teamMentorIdOrMentorId,
    );
  }
  return widgetDtoExMentorDto;
};

service.getWidgetDtoByWidgetId = (widgetDtos, widgetId) => {
  return widgetDtos?.find((widgetDto) => widgetDto.widgetId === widgetId);
};

service.getWidgetDtoExMentorDtoUsingMentorId = (widgetDtos, mentorId) => {
  let result = {};
  widgetDtos?.forEach((widgetDto) => {
    if (widgetDto.mentorsControlMapDto[mentorId]) {
      result.widgetDto = widgetDto;
      result.extendedMentorDto =
        widgetDto.mentorsControlMapDto[mentorId].extendedMentorDto;
    }
  });
  return result;
};

service.getWidgetDtoExMentorDtoUsingTeamMentorId = (widgetDtos, mentorId) => {
  let result = {};
  widgetDtos?.forEach((widgetDto) => {
    if (widgetDto.teamMentorsMapDto[mentorId]) {
      result.widgetDto = widgetDto;
      result.extendedMentorDto =
        widgetDto.teamMentorsMapDto[mentorId].extendedMentorDto;
    }
  });
  return result;
};

// don't use this function -> instead use the getWidgetDtoExMentorDtoUsingMentorId
service.getWidgetNameExMentorDtoUsingMentorId = (widgetDtos, mentorId) => {
  let result = {};
  widgetDtos?.forEach((widgetDto) => {
    if (widgetDto.mentorsControlMapDto[mentorId]) {
      result.widgetName = widgetDto.widgetAppCtrlDto.name;
      result.extendedMentorDto =
        widgetDto.mentorsControlMapDto[mentorId].extendedMentorDto;
    }
  });
  return result;
};

service.getTeamMentorDtoFromWidgetDto = (widgetDto) => {
  return Object.values(widgetDto?.teamMentorsMapDto || {})[0];
};

service.getWidgetTeamMentorIds = (widgetDto) => {
  return Object.keys(widgetDto?.teamMentorsMapDto ?? {});
};

service.shouldRequestForPrivateData = () => {
  let shouldRequest = true;
  let lastRequestTime = storageService.get(
    VIDABOO_CONSTANTS.LOCAL_STORAGE_ITEMS.LAST_DATA_PERMISSION_REQ_TIME,
  );
  if (lastRequestTime) {
    lastRequestTime = JSON.parse(lastRequestTime);
    shouldRequest =
      moment(lastRequestTime).format('DD') !== moment().format('DD');
  }
  return shouldRequest;
};

service.sortWidgetAndMentorCtrlMapDtos = (widgetDtos = []) => {
  let tempWidgetDtos = JSON.parse(JSON.stringify(widgetDtos));
  tempWidgetDtos.sort(service.sortWidgetDtos);
  //sorting the mentorsControlMapDto and teamMentorsMapDto within each widgetDto
  tempWidgetDtos = tempWidgetDtos.map((widgetDto) => {
    const mentorsControlMapDto = Object.fromEntries(
      Object.entries(widgetDto.mentorsControlMapDto).sort(
        ([, widgetMentorCtrlDto1], [, widgetMentorCtrlDto2]) =>
          service.sortWidgetMentorsControlMapDtoByProfileName(
            widgetMentorCtrlDto1,
            widgetMentorCtrlDto2,
          ),
      ),
    );
    const teamMentorsMapDto = Object.fromEntries(
      Object.entries(widgetDto.teamMentorsMapDto ?? {}).sort(
        ([, teamMentorCtrlDto1], [, teamMentorCtrlDto2]) =>
          service.sortWidgetMentorsControlMapDtoByProfileName(
            teamMentorCtrlDto1,
            teamMentorCtrlDto2,
          ),
      ),
    );
    return {...widgetDto, mentorsControlMapDto, teamMentorsMapDto};
  });
  return tempWidgetDtos;
};

service.sortWidgetDtos = (widgetDto1 = {}, widgetDto2 = {}) => {
  const widgetDto1Name = widgetDto1.widgetAppCtrlDto.name;
  const widgetDto2Name = widgetDto2.widgetAppCtrlDto.name;

  if (
    Object.keys(widgetDto1.mentorsControlMapDto).length === 0 &&
    Object.keys(widgetDto2.mentorsControlMapDto).length === 0
  )
    return widgetDto1Name?.localeCompare(widgetDto2Name);
  if (Object.keys(widgetDto1.mentorsControlMapDto).length === 0) return 1;
  if (Object.keys(widgetDto2.mentorsControlMapDto).length === 0) return -1;

  return widgetDto1Name?.localeCompare(widgetDto2Name);
};

service.sortWidgetMentorsControlMapDtoByMentorOwnerType = (
  widgetMentorCtrlDto1 = {},
  widgetMentorCtrlDto2 = {},
) => {
  const mentor1OwnerType =
    widgetMentorCtrlDto1.extendedMentorDto.mentorDto?.mentorOwnerType;
  const mentor2OwnerType =
    widgetMentorCtrlDto2.extendedMentorDto.mentorDto?.mentorOwnerType;
  if (
    mentor1OwnerType === VIDABOO_CONSTANTS.MENTOR_OWNER_TYPE.PERSONAL &&
    mentor2OwnerType === VIDABOO_CONSTANTS.MENTOR_OWNER_TYPE.WIDGET
  ) {
    return -1;
  }

  if (
    mentor1OwnerType === VIDABOO_CONSTANTS.MENTOR_OWNER_TYPE.WIDGET &&
    mentor2OwnerType === VIDABOO_CONSTANTS.MENTOR_OWNER_TYPE.PERSONAL
  ) {
    return 1;
  }

  return 0;
};

service.sortWidgetMentorsControlMapDtoByProfileName = (
  widgetMentorCtrlDto1 = {},
  widgetMentorCtrlDto2 = {},
) => {
  const mentor1Name =
    widgetMentorCtrlDto1.extendedMentorDto.mentorDto?.displayName;
  const mentor2Name =
    widgetMentorCtrlDto2.extendedMentorDto.mentorDto?.displayName;
  return mentor1Name?.localeCompare(mentor2Name);
};

service.updateCommunicationNavElementsOrder = (
  widgetCenterCF,
  selectedWidgetDto,
  selectedMentorDto,
) => {
  if (
    !widgetCenterCF?.disableWidgetApps &&
    selectedWidgetDto &&
    selectedMentorDto
  ) {
    let communicationTabElements = [];
    if (selectedMentorDto) {
      const communicationNavElement =
        navigationService.getNavElementUsingUrl(COMMUNICATION_PATH);
      communicationTabElements.push(communicationNavElement);

      if (!selectedMentorDto.appControlDto.foregroundWidgets)
        selectedMentorDto.appControlDto.foregroundWidgets = [];
    }

    communicationTabElements.forEach((communicationTabElement) => {
      const foregroundWidgets =
        selectedMentorDto.appControlDto.foregroundWidgets;

      let activeChannels = [];
      let inActiveChannels = [];
      //communicationNavElement.children -> children[0] : Appearance, children[1] : Active Channels, children[2] : Inactive Channels

      const defaultActiveTabElements =
        communicationTabElement.children[1]?.children;
      const defaultInActiveTabElements =
        communicationTabElement.children[2]?.children;

      let allCommunicationChannels = [];
      if (defaultActiveTabElements) {
        allCommunicationChannels = [...defaultActiveTabElements];
      }
      if (defaultInActiveTabElements) {
        allCommunicationChannels = [
          ...allCommunicationChannels,
          ...defaultInActiveTabElements,
        ];
      }
      allCommunicationChannels.forEach((communicationChannel) => {
        if (
          foregroundWidgets.includes(
            communicationChannel.navMenuItemId.replace('COMMUNICATION_', ''),
          )
        ) {
          activeChannels.push(communicationChannel);
        } else {
          inActiveChannels.push(communicationChannel);
        }
      });
      foregroundWidgets.forEach((fw, fwIndex) => {
        const activeChannelIdx = activeChannels.findIndex(
          (aChannel) =>
            aChannel.navMenuItemId.replace('COMMUNICATION_', '') === fw,
        );
        if (activeChannelIdx > -1) {
          const removableChannel = activeChannels[activeChannelIdx];
          activeChannels.splice(activeChannelIdx, 1);
          activeChannels.splice(fwIndex, 0, removableChannel);
        }
      });

      if (defaultActiveTabElements) {
        communicationTabElement.children[1].children = activeChannels;
      }
      if (defaultInActiveTabElements) {
        communicationTabElement.children[2].children = inActiveChannels;
      }
    });
    navigationService.updateRootNavElement();
  }
};

service.updateDataBasedOnBetaFeatures = () =>
  /*   enabledBetaFeatures,
  activeNavElement, */
  {
    // let navData = [];
    // // disable AI features
    // const isAiFeatureEnabled = VIDABOO_CONSTANTS.TEST_ENV
    //   ? expertHomeService.getIsBetaFeatureEnabled(
    //       enabledBetaFeatures,
    //       VIDABOO_CONSTANTS.BETA_FEATURES.AI,
    //     )
    //   : false;
    // navData.push({
    //   url: 'widgetSetup/setUpInstallation/aiAssistant',
    //   isViewable: isAiFeatureEnabled,
    // });
    // navigationService.updateNavElementsIsViewable(navData);
    // navigationService.updateRootNavElement();
    // const shouldUpdateActiveNavElement =
    //   !isAiFeatureEnabled &&
    //   navigationService.isAncestorOf(activeNavElement, BUCKET_SUB_MENU_ITEMS.AI);
    // return shouldUpdateActiveNavElement;
  };

service.updateDataWhenSelectedMentorDtosChanged = (
  updatedSelectedMentorDtosObj,
  tmpWidgetDtosArr,
  // tmpSelectedTeamMentorDtosObj,
  accountDto,
  currentUserId,
) => {
  Object.values(updatedSelectedMentorDtosObj).forEach((updatedWidgetDto) => {
    let widgetDtoToBeUpdatedIndex = tmpWidgetDtosArr.findIndex(
      (wDto) => wDto.widgetId === updatedWidgetDto.widgetId,
    );
    if (widgetDtoToBeUpdatedIndex !== -1) {
      // update widgetDto
      tmpWidgetDtosArr[widgetDtoToBeUpdatedIndex] = updatedWidgetDto;
    }

    // // update selectedTeamMentorDto
    // Object.keys(tmpSelectedTeamMentorDtosObj).forEach((teamMentorId) => {
    //   if (
    //     tmpSelectedTeamMentorDtosObj[teamMentorId].widgetId ===
    //     updatedWidgetDto.widgetId
    //   ) {
    //     tmpSelectedTeamMentorDtosObj[teamMentorId] = updatedWidgetDto;
    //   }
    // });
  });

  service.addDefaultValuesToWidgetDtos(
    accountDto,
    tmpWidgetDtosArr,
    currentUserId,
  );

  return {
    updatedSelectedMentorDtosObj,
    tmpWidgetDtosArr,
    // tmpSelectedTeamMentorDtosObj,
  };
};

// service.updateDataWhenSelectedTeamMentorDtosChanged = (
//   updatedSelectedTeamMentorDtosObj,
//   tmpWidgetDtosArr,
//   tmpSelectedMentorDtosObj,
//   accountDto,
//   currentUserId,
// ) => {
//   Object.values(updatedSelectedTeamMentorDtosObj).forEach(
//     (updatedWidgetDto) => {
//       let widgetDtoToBeUpdatedIndex = tmpWidgetDtosArr.findIndex(
//         (wDto) => wDto.widgetId === updatedWidgetDto.widgetId,
//       );
//       if (widgetDtoToBeUpdatedIndex !== -1) {
//         // update widgetDto
//         tmpWidgetDtosArr[widgetDtoToBeUpdatedIndex] = updatedWidgetDto;
//       }

//       // update selectedMentorDto
//       Object.keys(tmpSelectedMentorDtosObj).forEach((mentorId) => {
//         if (
//           tmpSelectedMentorDtosObj[mentorId].widgetId ===
//           updatedWidgetDto.widgetId
//         ) {
//           tmpSelectedMentorDtosObj[mentorId] = updatedWidgetDto;
//         }
//       });
//     },
//   );

//   service.addDefaultValuesToWidgetDtos(
//     accountDto,
//     tmpWidgetDtosArr,
//     currentUserId,
//   );

//   return {
//     updatedSelectedTeamMentorDtosObj,
//     tmpWidgetDtosArr,
//     tmpSelectedMentorDtosObj,
//   };
// };

service.updateDataWhenWidgetDtosChanged = (
  updatedWidgetDtos,
  tmpSelectedMentorDtosObj,
  // tmpSelectedTeamMentorDtosObj,
  accountDto,
  currentUserId,
) => {
  updatedWidgetDtos.forEach((updatedWidgetDto) => {
    // update selectedMentorDto
    Object.keys(tmpSelectedMentorDtosObj).forEach((mentorId) => {
      if (
        tmpSelectedMentorDtosObj[mentorId].widgetId ===
        updatedWidgetDto.widgetId
      ) {
        if (
          updatedWidgetDto.mentorsControlMapDto[mentorId] ||
          updatedWidgetDto.teamMentorsMapDto[mentorId]
        ) {
          tmpSelectedMentorDtosObj[mentorId] = updatedWidgetDto;
        } else {
          delete tmpSelectedMentorDtosObj[mentorId];
        }
      }
    });

    // // update selectedTeamMentorDto
    // Object.keys(tmpSelectedTeamMentorDtosObj).forEach((teamMentorId) => {
    //   if (
    //     tmpSelectedTeamMentorDtosObj[teamMentorId].widgetId ===
    //     updatedWidgetDto.widgetId
    //   ) {
    //     if (updatedWidgetDto.mentorsControlMapDto[teamMentorId]) {
    //       tmpSelectedTeamMentorDtosObj[teamMentorId] = updatedWidgetDto;
    //     } else {
    //       delete tmpSelectedMentorDtosObj[teamMentorId];
    //     }
    //   }
    // });
  });

  service.addDefaultValuesToWidgetDtos(
    accountDto,
    updatedWidgetDtos,
    currentUserId,
  );

  return {
    updatedWidgetDtos,
    // tmpSelectedTeamMentorDtosObj,
    tmpSelectedMentorDtosObj,
  };
};

service.updateLeftNavBarInitialData = (
  context,
  expertHomeContext,
  location,
  isLeftNavBarUpdated,
  setIsLeftNavBarUpdated,
) => {
  const {isAccountManager, accountDto, currentUser, billingPlanDto} = context;
  const {
    activeNavElement,
    backOfficeConfiguration,
    selectedMentorDtos,
    widgetDtos,
    setActiveNavElementByUrl,
  } = expertHomeContext;

  const backOffice = service.getControlledFeaturesAsBackOffice(
    context.currentUser.controlledFeaturesDto,
  );
  const personalSettingsCF = service.getControlledFeaturesAsBackOffice(
    context.currentUser.controlledFeaturesDto,
    'personalSettings',
  );
  const widgetCenterCF = service.getControlledFeaturesAsBackOffice(
    context.currentUser.controlledFeaturesDto,
    'widgetCenter',
  );
  const selectedWidgetDto =
    expertHomeService.getSelectedWidgetDto(selectedMentorDtos);
  const selectedMentorDto =
    expertHomeService.getSelectedMentorDto(selectedMentorDtos);

  // Hide tabs for users other than account manager
  service.updateCommunicationNavElementsOrder(
    widgetCenterCF,
    selectedWidgetDto,
    selectedMentorDto,
  );

  const isTeamAdmin = [
    VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.TEAM_ADMIN_SINGLE_MODE,
    VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.TEAM_ADMIN_MULTI_MODE,
  ].includes(backOfficeConfiguration);

  if (
    accountDto?.usersCtrlMap &&
    isAccountManager !== null &&
    backOfficeConfiguration
  ) {
    //show the old selection
    const shouldSetActiveNavElement =
      !activeNavElement && !location.state?.navUrl;

    let navData = [];

    const defaultUrl = 'home';

    //3D sense
    const extraFeatures = accountDto.isActive
      ? accountDto.extras
      : currentUser.extras;
    const threedEnabled =
      (extraFeatures && extraFeatures['THREED']) ||
      (billingPlanDto?.planEnabledFeatures &&
        billingPlanDto.planEnabledFeatures['THREED'] > 0);

    if (isAccountManager || isTeamAdmin) {
      // To handle the case if one widget is present with no agent
      if (
        widgetDtos?.length === 1 &&
        service.checkIfSingleWidgetWithNoAgentSkin(selectedMentorDtos)
      ) {
        navData.push(
          {
            url: 'widgetSetup/setUpInstallation',
            isViewable: false,
          },
          {
            url: 'settingsShortcuts',
            isViewable: false,
          },
        );
      } else {
        navData.push(
          {
            url: 'widgetSetup/setUpInstallation',
            isViewable: true,
          },
          {
            url: 'accountSettings',
            isViewable: isAccountManager,
          },
          {
            url: 'widgetSetup/setUpInstallation/styling/stylingTeam',
            isViewable: [
              VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.ADMIN_MULTI_MODE,
              VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.TEAM_ADMIN_MULTI_MODE,
            ].includes(backOfficeConfiguration),
          },
          {
            url: 'widgetSetup/setUpInstallation/control/controlAgent',
            isViewable: [
              VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.ADMIN_MULTI_MODE,
              VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.TEAM_ADMIN_MULTI_MODE,
            ].includes(backOfficeConfiguration),
          },
          {
            url: 'widgetSetup/setUpInstallation/customizations/customizationsAgent',
            isViewable: [
              VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.ADMIN_MULTI_MODE,
              VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.TEAM_ADMIN_MULTI_MODE,
            ].includes(backOfficeConfiguration),
          },
          {
            url: 'widgetSetup/setUpInstallation/customizations/customizationsTeam',
            isViewable: [
              VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.ADMIN_MULTI_MODE,
              VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.TEAM_ADMIN_MULTI_MODE,
            ].includes(backOfficeConfiguration),
          },
          {
            url: 'settingsShortcuts',
            isViewable: true,
          },
          {
            url: 'widgetSetup/setUpInstallation/aiAssistant',
            isViewable: true,
          },
        );
      }
      navigationService.updateNavElementsIsViewable(navData);
      navData = [];
    }
    if (!isAccountManager) {
      // can be team admin or non account manager
      if (
        backOfficeConfiguration ===
        VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.NON_ADMIN_EMPTY_WIDGET
      ) {
        navData.push(
          {
            url: 'widgetSetup',
            isViewable: false,
          },
          {url: 'accountSettings', isViewable: false},
          {
            url: 'settingsShortcuts',
            isViewable: false,
          },
        );
      } else {
        navData.push(
          {url: 'accountSettings', isViewable: false},
          {
            url: 'widgetSetup',
            isViewable:
              backOffice?.viewableTabs === undefined ||
              (backOffice?.viewableTabs &&
                backOffice.viewableTabs.includes(
                  VIDABOO_CONSTANTS.TABS_BACKEND_ID.WIDGET_CENTER,
                )),
          },
          {
            url: 'settingsShortcuts',
            isViewable: !widgetCenterCF?.disableWidgetApps,
          },
          {
            url: 'personalSettings/personalInformation',
            isViewable: !personalSettingsCF?.disablePersonalInfo,
          },
          {
            url: 'personalSettings/notifications',
            isViewable: !personalSettingsCF?.disableNotifications,
          },
          {
            url: 'personalSettings/integrations',
            isViewable: !personalSettingsCF?.disableIntegrations,
          },
          {
            url: 'widgetSetup/setUpInstallation/agentsList',
            isViewable: isTeamAdmin,
          },
          {
            url: 'widgetSetup/setUpInstallation/installation',
            isViewable: !widgetCenterCF?.disableWidgetScripts,
          },
          {
            url: 'widgetSetup/setUpInstallation/integrations',
            isViewable: !widgetCenterCF?.disableAdvancedFeatures,
          },
          {
            url: 'widgetSetup/setUpInstallation/control',
            isViewable:
              !widgetCenterCF?.disableAdvancedFeatures &&
              !(
                service.checkIfSelectedWidgetInactive(selectedMentorDtos) ||
                expertHomeService.checkIfSelectedAgentInactive(
                  selectedMentorDtos,
                )
              ),
          },
          {
            url: 'widgetSetup/setUpInstallation/communication',
            isViewable: !widgetCenterCF?.disableWidgetApps,
          },
          {
            url: 'widgetSetup/setUpInstallation/dynamicForms',
            isViewable: !widgetCenterCF?.disableWidgetApps,
          },
          {
            url: 'widgetSetup/setUpInstallation/paymentGateways',
            isViewable: !backOffice?.disablePaymentToMentor,
          },
          {
            url: `${COMMUNICATION_PATH}/communicationWebcall/moneyTransfer`,
            isViewable: !backOffice?.disablePaymentToMentor,
          },
          {
            url: `${COMMUNICATION_PATH}/communicationScheduleMeeting/moneyTransfer`,
            isViewable: !backOffice?.disablePaymentToMentor,
          },
          // todo : ENABLE_PAY_PER_LEAD : uncomment this
          // {
          //   url: `${COMMUNICATION_PATH}/communicationContactForm/moneyTransfer`,
          //   isViewable: !backOffice?.disablePaymentToMentor,
          // },
          {
            url: 'widgetSetup/setUpInstallation/styling',
            isViewable: !widgetCenterCF?.disableCustomStyling,
          },
          {
            url: 'widgetSetup/setUpInstallation/customizations',
            isViewable: !widgetCenterCF?.disableCustomStyling,
          },
          {
            url: 'widgetSetup/setUpInstallation/generalInfo',
            isViewable: !widgetCenterCF?.disablePersonalDetails,
          },
          {url: 'widgetSetup/manageWidget', isViewable: false},
          {
            url: 'widgetSetup/setUpInstallation/customizations/customizationsAgent',
            isViewable: true,
          },
          {
            url: 'widgetSetup/setUpInstallation/customizations/customizationsWidget',
            isViewable: !widgetCenterCF?.disableCustomStyling && isTeamAdmin,
          },
          {
            url: 'widgetSetup/setUpInstallation/customizations/customizationsTeam',
            isViewable: !widgetCenterCF?.disableCustomStyling && isTeamAdmin,
          },
          {
            url: 'widgetSetup/setUpInstallation/generalInfo/generalInfoWidgetView',
            isViewable: !widgetCenterCF?.disablePersonalDetails && isTeamAdmin,
          },
          {
            url: 'widgetSetup/setUpInstallation/control/controlGeneral',
            isViewable:
              !widgetCenterCF?.disableAdvancedFeatures &&
              !(
                service.checkIfSelectedWidgetInactive(selectedMentorDtos) ||
                expertHomeService.checkIfSelectedAgentInactive(
                  selectedMentorDtos,
                )
              ) &&
              isTeamAdmin,
          },
          {
            url: 'widgetSetup/setUpInstallation/styling/stylingWidget',
            isViewable: !widgetCenterCF?.disableCustomStyling && isTeamAdmin,
          },
          {
            url: 'widgetSetup/setUpInstallation/styling/stylingTeam',
            isViewable: !widgetCenterCF?.disableCustomStyling && isTeamAdmin,
          },
          {
            url: 'widgetSetup/setUpInstallation/aiAssistant',
            isViewable: true,
          },
        );
      }
      if (!service.getIsWidgetBrainAccessible(backOfficeConfiguration)) {
        // hide tabs which needs widget brain
        navData.push(
          {
            url: 'widgetSetup/setUpInstallation/aiAssistant/aiKnowledgeBase',
            isViewable: false,
          },
          {
            url: 'widgetSetup/setUpInstallation/aiAssistant/aiBrainModelConfiguration',
            isViewable: false,
          },
        );
      }
    }
    if (!threedEnabled) {
      navData.push({
        url: 'widgetSetup/setUpInstallation/threedSense',
        isViewable: false,
      });
    }
    navigationService.updateNavElementsIsViewable(navData);
    if (shouldSetActiveNavElement) {
      setActiveNavElementByUrl(defaultUrl);
    }
    if (!isLeftNavBarUpdated && setIsLeftNavBarUpdated) {
      setIsLeftNavBarUpdated(true);
    }
    navigationService.updateRootNavElement();
  }
};

service.updateNavElementsViewabilityByBackofficeConfigAndWidgetInteractionMode =
  (
    currentUser,
    activeNavElement,
    selectedWidgetDto,
    selectedMentorDtos,
    backOfficeConfiguration,
    setActiveNavElement,
  ) => {
    if (!selectedWidgetDto) return;

    let navData = [];
    let newActiveNavElement = null;

    const backOffice = service.getControlledFeaturesAsBackOffice(
      currentUser.controlledFeaturesDto,
    );

    // hide tabs if single widget is selected
    const isSingleWidgetSelected =
      Object.keys(selectedWidgetDto?.mentorsControlMapDto ?? {}).length === 1;
    const isTeamMentorDtoSelected =
      expertHomeService.getIsTeamMentorDtoSelected(selectedMentorDtos);

    const isBackOfficeMultiMode = [
      VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.ADMIN_MULTI_MODE,
      VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.TEAM_ADMIN_MULTI_MODE,
    ].includes(backOfficeConfiguration);

    // hide tabs when team mentor is selected
    navData.push(
      {
        url: 'widgetSetup/setUpInstallation/paymentGateways',
        isViewable:
          !isTeamMentorDtoSelected &&
          ([
            VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.ADMIN_MULTI_MODE,
            VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.ADMIN_SINGLE_MODE,
          ].includes(backOfficeConfiguration) ||
            !backOffice?.disablePaymentToMentor),
      },
      {
        url: `${COMMUNICATION_PATH}/communicationScheduleMeeting/moneyTransfer`,
        isViewable:
          !isTeamMentorDtoSelected &&
          ([
            VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.ADMIN_MULTI_MODE,
            VIDABOO_CONSTANTS.BACKOFFICE_CONFIGURATION.ADMIN_SINGLE_MODE,
          ].includes(backOfficeConfiguration) ||
            !backOffice?.disablePaymentToMentor),
      },
      {
        url: `${COMMUNICATION_PATH}/communicationWebcall`,
        isViewable: !isTeamMentorDtoSelected,
      },
      {
        url: 'settingsShortcuts/shortcutVideo',
        isViewable: !isTeamMentorDtoSelected,
      },
    );

    // team style tab
    const isStyleTeamTabAccessible =
      isBackOfficeMultiMode &&
      !isSingleWidgetSelected &&
      selectedWidgetDto.widgetAppCtrlDto.widgetInteractionMode !==
        VIDABOO_CONSTANTS.WIDGET_INTERACTION_MODE.TEAM_WIDGET;
    navData.push({
      url: 'widgetSetup/setUpInstallation/styling/stylingTeam',
      isViewable: isStyleTeamTabAccessible,
    });

    // team customization tab
    const isCustomizationsTabAccessible =
      isBackOfficeMultiMode && !isSingleWidgetSelected;
    navData.push({
      url: 'widgetSetup/setUpInstallation/customizations/customizationsTeam',
      isViewable: isCustomizationsTabAccessible,
    });

    // team ai settings tab
    const isAiSettingsWidgetTabAccessible =
      isBackOfficeMultiMode && !isSingleWidgetSelected;
    navData.push({
      url: 'widgetSetup/setUpInstallation/aiAssistant/aiSettings/aiSettingsWidget',
      isViewable: isAiSettingsWidgetTabAccessible,
    });

    navigationService.updateNavElementsIsViewable(navData);

    navigationService.updateRootNavElement();

    if (!activeNavElement) return;

    //update the activeNavElement if required
    if (
      [
        BUCKET_SUB_MENU_ITEMS.PAYMENT_GATEWAYS,
        BUCKET_SUB_MENU_ITEMS.COMMUNICATION_SCHEDULE_MONEY_TRANSFER,
        BUCKET_SUB_MENU_ITEMS.COMMUNICATION_WEBCALL_SETUP,
      ].includes(activeNavElement.navMenuItemId) &&
      isTeamMentorDtoSelected
    ) {
      const homeTabUrl = 'home';
      const appearanceOrderNavElement =
        navigationService.getNavElementUsingUrl(homeTabUrl);
      newActiveNavElement = appearanceOrderNavElement;
    }
    if (
      activeNavElement.navMenuItemId === BUCKET_SUB_MENU_ITEMS.STYLING_TEAM &&
      !isStyleTeamTabAccessible
    ) {
      const stylingTabUrl = 'widgetSetup/setUpInstallation/styling';
      const stylingNavElement =
        navigationService.getNavElementUsingUrl(stylingTabUrl);
      const firstChild = navigationService.getFirstChild(stylingNavElement);
      newActiveNavElement = firstChild;
    } else if (
      activeNavElement.navMenuItemId ===
        BUCKET_SUB_MENU_ITEMS.CUSTOMIZATIONS_TEAM &&
      !isCustomizationsTabAccessible
    ) {
      const customizationsTabUrl =
        'widgetSetup/setUpInstallation/customizations';
      const customizationNavElement =
        navigationService.getNavElementUsingUrl(customizationsTabUrl);
      const firstChild = navigationService.getFirstChild(
        customizationNavElement,
      );
      newActiveNavElement = firstChild;
    } else if (
      activeNavElement.navMenuItemId ===
        BUCKET_SUB_MENU_ITEMS.AI_SETTINGS_WIDGET &&
      !isAiSettingsWidgetTabAccessible
    ) {
      const aiSettingsTabUrl =
        'widgetSetup/setUpInstallation/aiAssistant/aiSettings';
      const aiSettingsNavElement =
        navigationService.getNavElementUsingUrl(aiSettingsTabUrl);
      const firstChild = navigationService.getFirstChild(aiSettingsNavElement);
      newActiveNavElement = firstChild;
    }
    if (newActiveNavElement && setActiveNavElement) {
      setActiveNavElement(newActiveNavElement);
    }
  };

const expertHomeService = {...service};
export default expertHomeService;
